import { CheckCircle2, Edit3, Plus, RefreshCw, Save, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';

const EMPTY_SOURCE = {
  name: '',
  type: 'rss',
  source_url: '',
  category: '',
  priority: 5,
  is_active: true,
};

const URL_LABELS = {
  rss: {
    label: 'Feed-URL',
    placeholder: 'https://example.de/feed.xml',
  },
  newsletter: {
    label: 'Absender-Email',
    placeholder: 'newsletter@example.de',
  },
  api: {
    label: 'API-URL',
    placeholder: 'https://api.example.de/v1/news',
  },
  manual: {
    label: 'URL oder Kennung',
    placeholder: 'https://example.de',
  },
};

function formatDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function SourceForm({ value, mode, onChange, onSubmit, onCancel, submitting }) {
  const urlConfig = URL_LABELS[value.type] || URL_LABELS.rss;

  function update(field, nextValue) {
    onChange({ ...value, [field]: nextValue });
  }

  return (
    <form className="source-form" onSubmit={onSubmit}>
      <div className="source-form-grid">
        <label>
          Name
          <input value={value.name} onChange={(event) => update('name', event.target.value)} required placeholder="Bayern Tourismus News" />
        </label>
        <label>
          Typ
          <select value={value.type} onChange={(event) => update('type', event.target.value)}>
            <option value="rss">RSS Feed</option>
            <option value="newsletter">Newsletter</option>
            <option value="api">API</option>
            <option value="manual">Manual</option>
          </select>
        </label>
        <label>
          {urlConfig.label}
          <input value={value.source_url || ''} onChange={(event) => update('source_url', event.target.value)} placeholder={urlConfig.placeholder} />
        </label>
        <label>
          Kategorien / Regionen
          <input value={value.category || ''} onChange={(event) => update('category', event.target.value)} placeholder="Bayern, Tourismus, Kampagne" />
        </label>
        <label>
          Prioritaet
          <input
            value={value.priority}
            min="1"
            max="10"
            onChange={(event) => update('priority', Number(event.target.value))}
            type="number"
          />
        </label>
        <label className="check-row source-active">
          <input checked={Boolean(value.is_active)} onChange={(event) => update('is_active', event.target.checked)} type="checkbox" />
          <span>Aktiv</span>
        </label>
      </div>
      <div className="source-form-actions">
        {mode === 'edit' ? (
          <button className="secondary-button" onClick={onCancel} type="button">
            <X size={16} />
            Abbrechen
          </button>
        ) : null}
        <button className="primary-button" disabled={submitting} type="submit">
          {mode === 'edit' ? <Save size={16} /> : <Plus size={16} />}
          {submitting ? 'Speichern...' : mode === 'edit' ? 'Speichern' : 'Quelle anlegen'}
        </button>
      </div>
    </form>
  );
}

export default function SourcesPage() {
  const [sources, setSources] = useState([]);
  const [draft, setDraft] = useState(EMPTY_SOURCE);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const stats = useMemo(() => {
    const active = sources.filter((source) => source.is_active).length;
    const newsletter = sources.filter((source) => source.type === 'newsletter').length;
    const rss = sources.filter((source) => source.type === 'rss').length;
    return { active, newsletter, rss, total: sources.length };
  }, [sources]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await api.sources();
      setSources(data.sources || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createSource(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.createSource(draft);
      setDraft(EMPTY_SOURCE);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(source) {
    setEditingId(source.id);
    setEditDraft({
      name: source.name || '',
      type: source.type || 'rss',
      source_url: source.source_url || '',
      category: source.category || '',
      priority: source.priority || 5,
      is_active: Boolean(source.is_active),
    });
  }

  async function saveEdit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.updateSource(editingId, editDraft);
      setEditingId(null);
      setEditDraft(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function deactivate(id) {
    setError('');
    try {
      await api.deactivateSource(id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function activate(source) {
    setError('');
    try {
      await api.updateSource(source.id, {
        is_active: true,
        metadata: {
          ...(source.metadata || {}),
          review_status: 'approved',
          approved_at: new Date().toISOString(),
        },
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>Quellen</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      <section className="metrics">
        <div>
          <strong>{stats.total}</strong>
          <span>Quellen gesamt</span>
        </div>
        <div>
          <strong>{stats.active}</strong>
          <span>Aktiv</span>
        </div>
        <div>
          <strong>{stats.rss}</strong>
          <span>RSS</span>
        </div>
        <div>
          <strong>{stats.newsletter}</strong>
          <span>Newsletter</span>
        </div>
      </section>

      {error ? <div className="alert">{error}</div> : null}

      <section className="source-panel">
        <div className="section-heading">
          <h2>Neue Quelle</h2>
          <p>RSS nutzt Feed-URLs, Newsletter nutzt die exakte Absender-Email als Match-Kriterium.</p>
        </div>
        <SourceForm value={draft} mode="create" onChange={setDraft} onSubmit={createSource} submitting={submitting} />
      </section>

      <section className="source-list">
        {loading ? <div className="screen-status">Quellen laden...</div> : null}
        {sources.map((source) => (
          <article className="source-row" key={source.id}>
            {editingId === source.id && editDraft ? (
              <SourceForm
                value={editDraft}
                mode="edit"
                onChange={setEditDraft}
                onSubmit={saveEdit}
                onCancel={() => {
                  setEditingId(null);
                  setEditDraft(null);
                }}
                submitting={submitting}
              />
            ) : (
              <>
                <div className="source-main">
                  <div className="source-title-row">
                    <span className={`status-pill ${source.is_active ? 'ready' : 'archived'}`}>{source.is_active ? 'aktiv' : 'inaktiv'}</span>
                    {source.metadata?.auto_detected ? <span className="status-pill in_progress">auto erkannt</span> : null}
                    <span className="type-pill">{source.type}</span>
                  </div>
                  <h2>{source.name}</h2>
                  <p>{source.source_url || 'Keine URL hinterlegt'}</p>
                  <small>
                    Kategorien: {source.category || '-'} · Prioritaet {source.priority} · Aktualisiert {formatDate(source.updated_at)}
                  </small>
                </div>
                <div className="source-actions">
                  {!source.is_active ? (
                    <button className="primary-button" onClick={() => activate(source)} type="button">
                      <CheckCircle2 size={16} />
                      Freigeben
                    </button>
                  ) : null}
                  <button className="secondary-button" onClick={() => startEdit(source)} type="button">
                    <Edit3 size={16} />
                    Bearbeiten
                  </button>
                  {source.is_active ? (
                    <button className="danger-button" onClick={() => deactivate(source.id)} type="button">
                      <X size={16} />
                      Deaktivieren
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
